import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser } from '@fortawesome/free-solid-svg-icons';
import api from '../Api';

/**
 * Profile photo of a prestataire / enquêteur.
 *
 * Photos are not public URLs: they are streamed by an authenticated endpoint, so the
 * image is fetched as a blob and shown through an object URL. Falls back to the person's
 * initials, and to a generic icon when there is no name either.
 *
 * `photoUpdatedAt` busts the cache when the person replaces their photo — the endpoint
 * itself answers 304 on revalidation, so re-renders stay cheap.
 */
const SIZES = {
  sm: { box: 'w-10 h-10', text: 'text-sm' },
  md: { box: 'w-16 h-16', text: 'text-lg' },
  lg: { box: 'w-24 h-24', text: 'text-2xl' },
  xl: { box: 'w-32 h-32', text: 'text-4xl' },
};

const initialsOf = (name) => {
  if (!name) return null;
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return null;
  return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
};

const ProfileAvatar = ({
  personType,        // 'supervisors' | 'interviewers'
  personId,
  name,
  photoUpdatedAt,    // ISO string | null — null means "no photo, don't fetch"
  size = 'md',
  className = '',
  self = false,      // fetch /profile/photo instead of /{id}/photo
}) => {
  const [src, setSrc] = useState(null);
  const [failed, setFailed] = useState(false);
  const dims = SIZES[size] || SIZES.md;

  useEffect(() => {
    let objectUrl = null;
    let cancelled = false;

    const load = async () => {
      if (!photoUpdatedAt || (!personId && !self)) {
        setSrc(null);
        return;
      }
      try {
        setFailed(false);
        const base = self
          ? `/${personType}/profile/photo`
          : `/${personType}/${personId}/photo`;
        // The photo is cached for a day under its URL, so the URL has to change when the
        // photo does — otherwise someone else replacing it (a chef terrain, say) would not
        // be visible to anyone whose browser already holds the old bytes.
        const url = `${base}?v=${encodeURIComponent(photoUpdatedAt)}`;
        const res = await api.get(url, { responseType: 'blob' });
        if (cancelled) return;
        objectUrl = URL.createObjectURL(res.data);
        setSrc(objectUrl);
      } catch (e) {
        if (!cancelled) {
          setFailed(true);
          setSrc(null);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [personType, personId, photoUpdatedAt, self]);

  const initials = initialsOf(name);

  return (
    <div
      className={`${dims.box} rounded-full overflow-hidden bg-red-100 flex items-center justify-center flex-shrink-0 ${className}`}
      title={name || undefined}
    >
      {src && !failed ? (
        <img src={src} alt={name || 'Photo de profil'} className="w-full h-full object-cover" />
      ) : initials ? (
        <span className={`font-semibold text-red-700 ${dims.text}`}>{initials}</span>
      ) : (
        <FontAwesomeIcon icon={faUser} className="text-red-600" />
      )}
    </div>
  );
};

export default ProfileAvatar;
